import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { articles, type ArticleItem } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const articleHeroImages: Record<string, string> = {
  'article-best-2025': 'https://readdy.ai/api/search-image?query=Gaming%20trophy%20and%20top%2010%20illustration%20with%20warm%20ambient%20lighting%2C%20editorial%20magazine%20style%2C%20soft%20gradient%20background%2C%20golden%20orange%20tones%2C%20professional%20composition&width=1200&height=500&seq=art-hero-best-2025&orientation=landscape',
  'article-safe-guide': 'https://readdy.ai/api/search-image?query=Digital%20security%20shield%20illustration%20with%20warm%20protective%20glow%2C%20minimalist%20tech%20editorial%20style%2C%20soft%20gradient%20background%2C%20orange%20and%20warm%20tones%2C%20clean%20composition&width=1200&height=500&seq=art-hero-safe-guide&orientation=landscape',
  'article-mobile-guide': 'https://readdy.ai/api/search-image?query=Smartphone%20gaming%20illustration%20with%20warm%20soft%20lighting%2C%20modern%20editorial%20style%2C%20clean%20gradient%20background%2C%20orange%20and%20peach%20tones%2C%20professional%20composition&width=1200&height=500&seq=art-hero-mobile-guide&orientation=landscape',
  'article-pc-games': 'https://readdy.ai/api/search-image?query=PC%20gaming%20setup%20illustration%20with%20warm%20ambient%20lighting%2C%20gaming%20editorial%20style%2C%20soft%20gradient%20background%2C%20orange%20and%20warm%20tones%2C%20professional%20composition&width=1200&height=500&seq=art-hero-pc-games&orientation=landscape',
  'article-visual-novel': 'https://readdy.ai/api/search-image?query=Visual%20novel%20book%20illustration%20with%20warm%20storytelling%20lighting%2C%20editorial%20bookmark%20style%2C%20soft%20gradient%20background%2C%20orange%20and%20pink%20tones%2C%20clean%20composition&width=1200&height=500&seq=art-hero-visual-novel&orientation=landscape',
  'article-beginner': 'https://readdy.ai/api/search-image?query=Beginner%20guide%20book%20illustration%20with%20warm%20welcoming%20lighting%2C%20editorial%20how-to%20style%2C%20soft%20gradient%20background%2C%20orange%20and%20warm%20tones%2C%20clean%20composition&width=1200&height=500&seq=art-hero-beginner&orientation=landscape',
};

const relatedArticles: Record<string, string[]> = {
  'best-adult-games-2025-top10': ['safe-adult-game-download-guide', 'mobile-adult-game-download-guide'],
  'safe-adult-game-download-guide': ['best-adult-games-2025-top10', 'adult-game-beginner-guide'],
  'mobile-adult-game-download-guide': ['safe-adult-game-download-guide', 'pc-adult-game-download-recommendations'],
  'pc-adult-game-download-recommendations': ['safe-adult-game-download-guide', 'mobile-adult-game-download-guide'],
  'visual-novel-adult-game-recommendations': ['best-adult-games-2025-top10', 'pc-adult-game-download-recommendations'],
  'adult-game-beginner-guide': ['safe-adult-game-download-guide', 'best-adult-games-2025-top10'],
};

function renderContent(content: string) {
  return content.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;

    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-8 mb-4">{trimmed.replace('## ', '')}</h2>;
    }
    if (trimmed.startsWith('### ')) {
      return <h3 key={i} className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mt-6 mb-3">{trimmed.replace('### ', '')}</h3>;
    }
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return <p key={i} className="text-sm md:text-base font-medium text-foreground-800 my-3">{trimmed.replace(/\*\*/g, '')}</p>;
    }
    if (trimmed.startsWith('- ')) {
      return <li key={i} className="text-sm md:text-base text-foreground-700 ml-4 list-disc">{trimmed.replace('- ', '')}</li>;
    }
    if (/^\d\./.test(trimmed)) {
      return <li key={i} className="text-sm md:text-base text-foreground-700 ml-4 list-decimal">{trimmed.replace(/^\d\.\s*/, '')}</li>;
    }
    return <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed my-2">{trimmed}</p>;
  });
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleItem | null>(null);
  const [related, setRelated] = useState<ArticleItem[]>([]);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug);
    if (found) {
      setArticle(found);
      const relSlugs = relatedArticles[found.slug] || [];
      const relArticles = articles.filter((a) => relSlugs.includes(a.slug));
      setRelated(relArticles);
      document.title = `${found.title} | 黄游下载`;
    } else {
      setArticle(null);
      setRelated([]);
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center pt-32 pb-20">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-warning-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">文章不存在</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-4 inline-block whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://huangyou-download.com/article/${article.slug}`,
    about: article.tags,
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Article Hero */}
          <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
            <img
              src={articleHeroImages[article.imageSeq] || articleHeroImages['article-best-2025']}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
            <div className="relative z-10 w-full max-w-3xl mx-auto px-4 text-center pt-16">
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center justify-center gap-4 text-white/75 text-xs">
                <span>{article.author}</span>
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </section>

          {/* Breadcrumb */}
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '文章资讯', href: '/article/best-adult-games-2025-top10' },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Content */}
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Content */}
              <div className="prose-custom">
                {renderContent(article.content)}
              </div>
            </div>

            {/* Article Footer */}
            <div className="mt-6 pt-6 border-t border-background-200/60">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-500">
                  发布日期: {article.date}
                </span>
                <Link
                  to="/"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
                >
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-left-line text-xs"></i></span>
                  返回首页
                </Link>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {related.length > 0 && (
            <section className="py-10 md:py-12 bg-background-100">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {related.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                        <img
                          src={articleHeroImages[rel.imageSeq] || articleHeroImages['article-best-2025']}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </div>
    </>
  );
}